"use client";
import { useEffect, useState, useCallback, useMemo } from "react";
import Link from "next/link";
import { Loader2, Check } from "lucide-react";
import Image from "next/image";
import { Button } from "@/app/components/shadcn/button";
import FallbackImage from "@/app/components/elements/FallbackImage";
import FavoriteButton from "@/app/components/elements/buttons/FavoriteButton";
import ProductCardInfo from "@/app/components/elements/cards/productCard/ProductCardInfo";
import ProductCardPrice from "@/app/components/elements/cards/productCard/ProductCardPrice";
import ProductCardMobileCartSheet from "@/app/components/elements/cards/productCard/ProductCardMobileCartSheet";
import { buildSocialProofItems, buildCampaignItems, normalizeSizes } from "@/app/utils/productCard.utils";
import { getSoftBadgeStyle } from "@/app/utils/badgeColors";
import usePersonalizedProducts from "@/app/lib/features/personalizedProducts/personalizedProducts.hook";
import useCart from "@/app/lib/features/cart/cart.hook";
import useModal from "@/app/lib/features/modal/modal.hook";
import { Product } from "@/app/lib/features/product/product.types";
import { cn } from "@/app/utils/cn";
import { toast } from "sonner";
import { trackProductClick, trackProductAddToCart } from "@/app/lib/tracking/interactions";

/* ── Yatay ürün kartı ─────────────────────────────────────────────── */
const BasketHorizontalCard = ({ product }: { product: Product }) => {
  const { addToCart } = useCart();
  const { createModal } = useModal();
  const [cartLoading, setCartLoading] = useState(false);
  const [cartDone, setCartDone] = useState(false);
  const [isMobileCartOpen, setIsMobileCartOpen] = useState(false);

  const socialProofItems = useMemo(() => buildSocialProofItems(product), [product]);
  const campaignItems = useMemo(() => buildCampaignItems(product), [product]);
  const allSizes = useMemo(() => normalizeSizes(product), [product]);

  const firstSizeVariantId =
    product.variants_by_color?.[0]?.sizes?.[0]?.variant_id ??
    product.variants_by_color?.[0]?.variant_id ??
    product.variant_id;

  const priceNum = parseFloat(product.price.replace(/\./g, "").replace(",", ".")) || 0;

  const handleAddToCart = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (cartLoading) return;

      // Birden fazla beden varsa → mobile sheet / desktop modal aç
      if (allSizes.length > 1) {
        if (window.innerWidth >= 1024) {
          createModal("product-card-cart", { product });
        } else {
          setIsMobileCartOpen(true);
        }
        return;
      }

      if (!firstSizeVariantId) {
        toast.error("Lütfen beden seçiniz");
        return;
      }

      setCartLoading(true);
      const result = await addToCart(
        { product_id: product.id, variant_id: firstSizeVariantId, quantity: 1 },
        false,
        { name: product.internet_product_description || product.name, price: priceNum, brand: product.brand_name },
      );

      if (result.success) {
        trackProductAddToCart(product.id);
        createModal("added-to-cart", {
          image: product.first_image ?? "",
          name: product.internet_product_description || product.name,
          price: `${(result.price ?? priceNum).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`,
        });
        setCartDone(true);
        setTimeout(() => { setCartDone(false); setCartLoading(false); }, 2000);
      } else {
        toast.error(result.message ?? "Sepete eklenemedi");
        setCartLoading(false);
      }
    },
    [cartLoading, allSizes, firstSizeVariantId, product, priceNum, addToCart, createModal],
  );

  return (
    <article className="relative w-full overflow-hidden group/card">
      {/* Favori — absolute top-right, aynı ProductCard konumu */}
      <div className="absolute top-1 right-1 sm:top-2 sm:right-2 z-20">
        {/* Favori renk varyantı bazında: kartın gösterdiği varyant ve o
            varyantın is_favorited değeri (ürün seviyesindeki değil). */}
        <FavoriteButton
          productId={product.id}
          variantId={product.variant_id}
          initialIsFavorited={
            product.variants_by_color?.find(
              (v) => v.variant_id === product.variant_id,
            )?.is_favorited ??
            product.is_favorited ??
            false
          }
        />
      </div>

      <Link
        href={`/${product.slug}`}
        onClick={() => trackProductClick(product.id)}
        className="flex gap-0 bg-white hover:bg-gray-50 border border-gray-200 hover:border-main hover:shadow-md transition-all duration-300 ease-in-out rounded-lg overflow-hidden"
      >
        {/* Görsel */}
        <div className="relative w-24 sm:w-28 shrink-0 aspect-[2/3]">
          <div className="absolute inset-0">
            <FallbackImage
              src={product.first_image || ""}
              alt={product.internet_product_description || product.name}
              fill
              className="object-cover group-hover/card:scale-[1.02] transition-all duration-300 ease-in-out"
              sizes="(max-width: 640px) 96px, 112px"
              quality={70}
            />
          </div>
        </div>

        {/* Sağ alan */}
        <div className="flex flex-col flex-1 min-w-0 p-2">
          <ProductCardInfo
            brandName={product.brand_name}
            productName={product.internet_product_description || product.name}
            productDescription={product.internet_product_description || ""}
            ratingAverage={product.rating?.average ?? 0}
            ratingCount={product.rating?.count ?? 0}
            socialProofItems={socialProofItems}
            campaignItems={campaignItems}
          />

          {/* Static badges — yan yana, animasyonsuz */}
          {product.badges && product.badges.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {product.badges.map((badge, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-medium"
                  style={getSoftBadgeStyle(badge.color_hex)}
                >
                  {badge.icon_full && (
                    <Image src={badge.icon_full} alt={badge.label} width={10} height={10} className="shrink-0" unoptimized />
                  )}
                  {badge.label}
                </span>
              ))}
            </div>
          )}

          <div className="flex items-end justify-between gap-2 mt-1.5">
            <ProductCardPrice
              price={product.price}
              listPrice={product.list_price ?? ""}
              hasDiscount={product.has_discount}
              hasCartSpecialPrice={product.has_cart_special_price}
              cartSpecialPriceText={product.cart_special_price_text}
              currencySymbol={product.currency_symbol}
            />
            <Button
              variant="outline"
              disabled={cartLoading}
              onClick={handleAddToCart}
              className="shrink-0 text-xs px-2 gap-1"
            >
              {cartLoading ? (
                cartDone
                  ? <><Check className="h-3 w-3" />Eklendi</>
                  : <><Loader2 className="h-3 w-3 animate-spin" />Ekleniyor</>
              ) : "Sepete Ekle"}
            </Button>
          </div>
        </div>
      </Link>

      <ProductCardMobileCartSheet
        product={product}
        isOpen={isMobileCartOpen}
        onClose={() => setIsMobileCartOpen(false)}
      />
    </article>
  );
};

/* ── Skeleton ─────────────────────────────────────────────────────── */
const CardSkeleton = () => (
  <div className="flex border border-gray-100 rounded-lg overflow-hidden bg-white">
    <div className="w-24 sm:w-28 shrink-0 aspect-[2/3] bg-gray-100 animate-pulse" />
    <div className="flex flex-col gap-2 flex-1 p-2">
      <div className="h-2.5 w-1/2 rounded bg-gray-100 animate-pulse" />
      <div className="h-2.5 w-full rounded bg-gray-100 animate-pulse" />
      <div className="h-2.5 w-3/4 rounded bg-gray-100 animate-pulse" />
      <div className="flex justify-between items-end mt-auto pt-2">
        <div className="h-4 w-1/4 rounded bg-gray-100 animate-pulse" />
        <div className="h-6 w-20 rounded-lg bg-gray-100 animate-pulse" />
      </div>
    </div>
  </div>
);

/* ── Ana bileşen ──────────────────────────────────────────────────── */
const BasketPersonalizedSection = () => {
  const { groups, isLoading, fetchPersonalizedProducts } = usePersonalizedProducts();
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchPersonalizedProducts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeItems: Product[] = groups[activeTab]?.items ?? [];

  if (!isLoading && groups.length === 0) return null;

  return (
    <div className="mt-6 space-y-4">
      {/* Tab bar */}
      <div className="relative border-b border-gray-200 overflow-hidden">
        <div className="flex overflow-x-auto scrollbar-none">
          {isLoading
            ? [1, 2].map((i) => (
                <div key={i} className="h-4 w-28 rounded bg-gray-100 animate-pulse mr-6 mb-3 shrink-0" />
              ))
            : groups.map((group, idx) => (
                <button
                  key={group.key}
                  type="button"
                  onClick={() => setActiveTab(idx)}
                  className={cn(
                    "text-sm font-medium pb-3 mr-6 border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0",
                    activeTab === idx
                      ? "border-main text-main"
                      : "border-transparent text-gray-400 hover:text-gray-700",
                  )}
                >
                  {group.title}
                </button>
              ))}
        </div>
      </div>

      {/* Grid: 1 → 2 → 3 sütun */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {isLoading
          ? Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)
          : activeItems.map((product) => (
              <BasketHorizontalCard key={product.id} product={product} />
            ))}
      </div>
    </div>
  );
};

export default BasketPersonalizedSection;